/**
 * UK final tax export — single source for CSV, SA108 summary, and UI top cards.
 */

import {
  pickFiatBuy,
  pickFiatSell,
  pickFiatTotal,
  pickFiatTaxable,
  pickFiatGrossProfit,
  isBlockedTaxRow,
} from "./fiatFields.js";
import {
  isSyntheticVthoRow,
  pickDisplayHash,
  pickExportVthoTokenAmount,
  pickSyntheticVthoIncomeFiat,
} from "./syntheticVthoDisplay.js";
import { applyVthoGeneratedDisplayRollup } from "./rollupGeneratedVthoByDay.js";
import { formatUkTaxYearLabel, getUkTaxYearFromTimestamp, toUnixSecondsLoose } from "./taxYear.js";
import {
  hasUsableUkFinalMoney,
  pickUkEventUniq,
  normalizeUkIncomeEventKeyForDedup,
  resolveUkCapitalEventKey,
} from "./ukTaxUi.js";
import { utcCalendarDayDiff, utcDayKey } from "./ukCalendarDays.js";
import { isNftCapitalDisposalClass, isActualTaxReviewRow } from "../../shared/taxExportEligibility.js";
import { extractEventIdentityFields } from "./exportFormatting.js";

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

export function normalizeExportComparableValue(value) {
  if (value === null || value === undefined) return "";
  const n = Number(value);
  if (Number.isFinite(n)) return n.toFixed(12).replace(/\.?0+$/, "");
  return String(value).trim().toLowerCase();
}

function toNum(v) {
  if (v === null || v === undefined) return NaN;
  if (typeof v === "number") return Number.isFinite(v) ? v : NaN;
  let s = String(v).trim();
  if (!s) return NaN;
  s = s.replace(/[€£$\s]/g, "").replace(/EUR|USD|GBP/gi, "");
  if (s.includes(",") && s.includes(".")) {
    s = s.replace(/\./g, "").replace(",", ".");
  } else if (s.includes(",") && !s.includes(".")) {
    s = s.replace(",", ".");
  }
  const n = Number(s);
  return Number.isFinite(n) ? n : NaN;
}

function firstFinite(...vals) {
  for (const v of vals) {
    const n = toNum(v);
    if (Number.isFinite(n)) return n;
  }
  return NaN;
}

function toSec(ts) {
  const n = toUnixSecondsLoose(ts);
  return n > 0 ? n : 0;
}

function fmtUtc(tsSec) {
  const ts = Number(tsSec || 0);
  if (!ts) return "";
  const d = new Date(ts * 1000);
  return d.toLocaleString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
    timeZone: "UTC",
  });
}

function rowInUkTaxYear(tsSec, yearInt) {
  if (!yearInt || !tsSec) return false;
  return getUkTaxYearFromTimestamp(tsSec) === yearInt;
}

function normExportEventKey(row = {}) {
  const hash = String(row.Tx_Hash || "").trim().toLowerCase();
  const ek = String(row.Event_Key || "").trim().toLowerCase();
  return normalizeUkIncomeEventKeyForDedup(ek, hash) || ek || hash;
}

/** Dedupe only when the normalized event key is identical (same on-chain event). */
export function dedupeExportRowsByEventKey(rows = []) {
  const seen = new Set();
  const out = [];
  for (const row of Array.isArray(rows) ? rows : []) {
    const key = normExportEventKey(row);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(row);
  }
  return out;
}

/** @deprecated Use dedupeExportRowsByEventKey — economic fingerprint dedupe removed. */
export function dedupeFinalExportRows(rows = [], moneyKeys = []) {
  return dedupeExportRowsByEventKey(rows);
}

function isInflowsByTokenEventKey(eventKey = "") {
  const k = String(eventKey || "").toLowerCase();
  return k.includes("#inflowsbytoken:") || k.startsWith("inflowsbytoken:");
}

const UK_INCOME_MULTIPLICITY_CLASSES = new Set([
  "mva_staking_rewards",
  "stargate_staking_rewards",
  "thorhead_staking_rewards",
  "vemugs_staking_rewards",
  "vemugs_mint_reward",
  "vemugs_reward_distribution",
]);

function incomeAliasFingerprint(row = {}, incomeKey, taxableKey) {
  return [
    row.Steuerjahr,
    row.Quelle,
    row.Klassifizierung,
    row.Asset,
    normalizeExportComparableValue(row.Menge),
    row.Datum_UTC,
    String(row.Tx_Hash || "").trim().toLowerCase(),
    normalizeExportComparableValue(row[taxableKey] ?? row[incomeKey]),
  ]
    .map((v) => String(v ?? "").trim().toLowerCase())
    .join("|");
}

function isUkIncomeMultiplicityExportRow(row = {}) {
  const cls = String(row.Klassifizierung || "").toLowerCase();
  const ek = String(row.Event_Key || "").toLowerCase();
  return UK_INCOME_MULTIPLICITY_CLASSES.has(cls) || ek.includes("|occ");
}

function incomeRowCanonicalScore(row = {}) {
  const ek = String(row.Event_Key || "").toLowerCase();
  if (ek.includes("|occ")) return 0;
  if (ek.includes("#row")) return 1;
  if (!isInflowsByTokenEventKey(ek) && ek.includes("#") && !ek.endsWith("#0")) return 2;
  if (ek.endsWith("#0")) return 20;
  if (isInflowsByTokenEventKey(ek)) return 30;
  return 10;
}

/**
 * Collapse alias duplicates (#row + #0 + inflowsByToken) for the same real income event.
 * Preserve true multiplicity (MVA/Stargate: multiple ledger rows per hash+amount).
 */
export function dedupeUkIncomeAliasRows(rows = [], incomeKey, taxableKey) {
  const groups = new Map();

  for (const row of Array.isArray(rows) ? rows : []) {
    const fp = incomeAliasFingerprint(row, incomeKey, taxableKey);
    if (!groups.has(fp)) groups.set(fp, []);
    groups.get(fp).push(row);
  }

  const out = [];

  for (const group of groups.values()) {
    if (group.length === 1) {
      out.push(group[0]);
      continue;
    }

    if (group.some(isUkIncomeMultiplicityExportRow)) {
      const seen = new Set();
      for (const row of group) {
        const normKey = normExportEventKey(row);
        if (seen.has(normKey)) continue;
        seen.add(normKey);
        out.push(row);
      }
      continue;
    }

    const [best] = [...group].sort(
      (a, b) => incomeRowCanonicalScore(a) - incomeRowCanonicalScore(b)
    );
    out.push(best);
  }

  return out;
}

function hasHardTaxBlocker(row = {}) {
  if (row == null || typeof row !== "object") return false;

  return Boolean(
    row.includedInTaxTotals === false ||
      row.excludedFromTaxTotals === true ||
      row.missingCostBasis === true ||
      row.costBasisUnknown === true ||
      row.cost_basis_unknown === true ||
      row.requiresUserCostBasis === true ||
      row.userCostBasisRequired === true ||
      row.taxComputationSkipped === true ||
      row.isTaxComputationBlocked === true ||
      row.pricingMissing === true ||
      row.priceMissing === true ||
      row.missingFiatPrice === true ||
      isBlockedTaxRow(row)
  );
}

function isUkExportableB3trReward(row = {}) {
  const cls = String(row.classification || row.type || "").toLowerCase();
  const token = String(row.token || row.asset || "").toUpperCase();
  if (token !== "B3TR" && token !== "VEB3TR") return false;
  if (cls !== "airdrop" && !cls.includes("x2earn")) return false;
  return hasUsableUkFinalMoney(row);
}

function isExcludedFromFinalTaxExport(row = {}, isUK = true, context = "income") {
  if (row == null || typeof row !== "object") return false;

  const displayStatus = String(row?.displayPriceStatus || "").toLowerCase();
  const pricingStatus = String(row?.pricingStatus || "").toLowerCase();
  const taxStatus = String(row?.taxStatus || "").toLowerCase();
  const taxableStatus = String(row?.taxableStatus || "").toLowerCase();
  const taxResolutionStatus = String(row?.taxResolutionStatus || "").toLowerCase();
  const acquisitionStatus = String(row?.acquisitionStatus || "").toLowerCase();
  const reviewReason = String(row?.reviewReason || "").toLowerCase();
  const pricingReason = String(row?.pricingReason || "").toLowerCase();

  // B3TR/VEB3TR rewards (vip180/DE-Pfad): needsReview darf Income-Export nicht blockieren.
  if (isUK && context === "income" && isUkExportableB3trReward(row)) return false;

  if (hasHardTaxBlocker(row)) return true;
  // Capital: DE-Parität — kein Bypass nur wegen proceeds vorhanden.
  if (isUK && context === "income" && hasUsableUkFinalMoney(row)) return false;

  if (row.needsReview || row.reviewRequired || row.requiresManualReview) return true;

  const joined = [
    displayStatus,
    pricingStatus,
    taxStatus,
    taxableStatus,
    taxResolutionStatus,
    acquisitionStatus,
    reviewReason,
    pricingReason,
  ].join("|");

  return (
    joined.includes("price_not_available") ||
    joined.includes("missing_price") ||
    joined.includes("token_price_not_available") ||
    joined.includes("cost_basis_unknown") ||
    joined.includes("cost_basis_not_available") ||
    joined.includes("unknown_cost_basis") ||
    joined.includes("needs_cost_basis_review") ||
    joined.includes("manual_review") ||
    joined.includes("review_required")
  );
}

function pickExclusionReason(row = {}) {
  if (row == null || typeof row !== "object") {
    return "excluded_from_tax_totals";
  }
  if (row.fifoShortage) return "fifo_shortage";
  if (row.taxResolutionStatus === "needs_receipt_allocation") return "receipt_allocation_required";
  if (row.pricingReason) return String(row.pricingReason).toLowerCase();
  if (row.reviewReason) return String(row.reviewReason).toLowerCase();
  if (row.missingFiatPrice || row.pricingMissing || row.priceMissing) {
    return "token_price_not_available";
  }
  if (row.missingCostBasis || row.costBasisUnknown) return "cost_basis_unknown";
  return "excluded_from_tax_totals";
}

const UK_TOKEN_DISPOSAL_TYPES = new Set([
  "sent",

  "swap_out",
  "nfbc_purchase",
  "v3tax_purchase",
  "mugshot_pro_subscription",
  "vewin_4ocean_donation",
  "one_million_pixel_purchase",
  "golden_empire_spend",
  "golden_empire_burn_spend",
  "vaiyans_vco_game_entry",
  "squad_game_spend",
  "squad_vtho_game_entry",
  "squad_nft_upgrade_spend",
  "squad_preico_payment",
  "juicyfinance_liquidation_debt_payment",
  "groncard_gift_card_purchase",
  "pause_your_carbon_purchase",
  "gas_fee",
  "gas_abstraction_fee",
  "dex_aggregator_execution_cost",
  "dex_aggregator_fee",
  "vaultx_loan_repayment_fee",
  "vet_domains_registration",
  "vet_domains_renewal",
  "vaiyans_vco_prediction_entry",
  "velottery_ticket_purchase",
  "scoopup_puppy_pass_fee",
]);

function isUkJuicyDefiCapitalDisposal(cls = "") {
  return /^juicyfinance_[a-z0-9]+_(repay|deposit|withdrawal)$/.test(
    String(cls || "").trim().toLowerCase()
  );
}

/** Payment-token disposals for NFT mint/acquire (fungible leg, not NFT identifiable). */
const UK_NFT_PAYMENT_TOKEN_DISPOSAL_TYPES = new Set([
  "nft_mint",
  "nft_galaxy_mint",
  "mint",
  "galaxy_mint",
  "mint_payment",
  "nft_marketplace_purchase_payment",
  "nft_marketplace_pending_mint_payment",
]);

function isUkNftPaymentTokenDisposal(cls = "") {
  return UK_NFT_PAYMENT_TOKEN_DISPOSAL_TYPES.has(String(cls || "").toLowerCase());
}

export function isUkTokenCapitalDisposal(cls = "") {
  const c = String(cls || "").trim().toLowerCase();

  return (
    UK_TOKEN_DISPOSAL_TYPES.has(c) ||
    isUkJuicyDefiCapitalDisposal(c) ||
    isUkNftPaymentTokenDisposal(c)
  );
}

/**
 * Canonical UK eligibility for VET/VTHO/VIP-180 tax-table rows.
 *
 * PDF detail tables must use exactly the same exclusion decision as the
 * final UK Capital Gains / Miscellaneous Income exports.
 */
export function isUkFinalTokenTableRowExcluded(row = {}) {
  const cls = pickCls(row);

  const context = isUkTokenCapitalDisposal(cls)
    ? "capital"
    : "income";

  return isExcludedFromFinalTaxExport(
    row,
    true,
    context
  );
}

function pickCapitalSourceFromPaymentToken(token, fallback = "NFT") {
  const t = String(token || "").toUpperCase();
  if (t === "VET") return "VET";
  if (t === "VTHO") return "VTHO";
  if (t === "B3TR" || t === "VEB3TR" || t.startsWith("JUICY_SUPPLIED:")) {
    return "VIP-180";
  }
  return fallback;
}

function hasUkNftMintPaymentDisposalMoney(row = {}, legStats = {}) {
  if (hasUsableUkFinalMoney(row)) return true;

  const profit = pickFiatGrossProfit(row);
  if (Number.isFinite(profit) && Math.abs(Number(profit)) > 1e-12) return true;

  if (Number(legStats.fifoLots || 0) > 0) {
    return (
      Math.abs(Number(legStats.profitEUR || 0)) > 1e-12 ||
      Math.abs(Number(legStats.sellEUR || 0)) > 1e-12 ||
      Math.abs(Number(legStats.buyEUR || 0)) > 1e-12
    );
  }

  return false;
}

const UK_INCOME_TYPES = new Set([
  "staking_reward",
  "governance_reward",
  "airdrop",
  "solarwise_electricity_revenue",
  "foundation_bonus",
  "juicyfinance_lending_income",
  "juicyfinance_b3tr_reward_claim",
  "generated",
  "stargate_staking_rewards",
  "thorhead_staking_rewards",
  "vemugs_staking_rewards",
  "vemugs_mint_reward",
  "vemugs_reward_distribution",
  "verocket_generated_vtho_claim",
  "node_legacy_rewards",
  "vtho_synthetic",
  "x2earn_reward",
  "x2earn_reward_distribution",
  "x2earn_reward_claim",
  "vestation_reward_claim",
  "vexchange_farm_rewards",
  "betterswap_pool2earn_rewards",
  "mva_staking_rewards",
  "wov_staking_rewards",
  "wov_genesis_staking_rewards",
  "wov_genesis_staking_reward",
  "wov_genesis_reward",
  "genesis_staking_rewards",
  "golden_empire_reward",
  "safehaven_staking_rewards",
  "sagaz_reward_claim",
  "vfox_nft_holder_reward",
  "vesea_nft_staking_rewards",
  "union_nft_holder_rewards",
  "vimworld_veed_reward_distribution",
]);

function pickHash(r) {
  return String(r?.hash || r?.txid || r?.event_id || r?.txHash || "").trim();
}

function pickCls(r) {
  return String(r?.classification || r?.type || r?.kind || "").toLowerCase();
}

function pickToken(r) {
  return String(r?.tokenSymbol || r?.token || r?.base || r?.asset || "").toUpperCase();
}

function pickAmount(r) {
  const raw = firstFinite(r?.amount, r?.qty, r?.quantity, r?.lpAmount, r?.tokenAmount, 0) || 0;
  const exported = pickExportVthoTokenAmount(r);
  return exported != null && Number.isFinite(Number(exported)) ? exported : raw;
}

function pickRowTs(r) {
  return (
    r?.timestamp ??
    r?.ts ??
    r?.block_timestamp ??
    r?.blockTimestamp ??
    r?.sell_ts ??
    r?.sellTs ??
    r?.date ??
    0
  );
}

function normalizeIncomeCls(source, row) {
  const cls = pickCls(row);
  const token = pickToken(row);
  if (cls.includes("x2earn")) return "x2earn_reward_claim";
  if ((token === "B3TR" || token === "VEB3TR") && cls === "airdrop") return "x2earn_reward_claim";
  return cls;
}

function resolveRowSource(sectionSource, row) {
  const normalizedSection = String(sectionSource || "").toUpperCase();
  if (["B3TR", "VIP-180", "VIP180"].includes(normalizedSection)) return "VIP-180";
  const fromRow = row?.sourcePlatform || row?.application || row?.source;
  if (fromRow) {
    const upper = String(fromRow).trim().toUpperCase();
    if (["VIP180", "VIP-180", "B3TR"].includes(upper)) return "VIP-180";
    return String(fromRow).trim();
  }
  return sectionSource;
}

function normalizeUkMatchingRule(rule = "") {
  const r = String(rule || "").trim().toLowerCase();
  if (r === "same_day" || r === "same-day") return "same_day";
  if (
    r === "bed_and_breakfast_30d" ||
    r === "bed-and-breakfast-30d" ||
    r === "30_day" ||
    r === "30-day"
  ) {
    return "bed_and_breakfast_30d";
  }
  if (r === "section_104" || r === "section104" || r === "s104") return "section_104";
  if (r === "nft_identifiable" || r === "uk_nft_identifiable") return "nft_identifiable";
  return r;
}

/** UK matching stats for capital export columns. */
export function computeUkLegMatchingStats(row = {}, disposalTsSec = 0) {
  const legs = Array.isArray(row?.fifoLegs) ? row.fifoLegs : [];
  if (!legs.length) {
    return {
      fifoLots: 0,
      offsetMin: "",
      offsetMax: "",
      acqMin: "",
      acqMax: "",
    };
  }

  const offsets = [];
  const acqLabels = [];

  for (const leg of legs) {
    const rule = normalizeUkMatchingRule(
      leg?.matchingRule || leg?.ukMatchingRule || row?.matchingRule || ""
    );
    const acqTs = toSec(
      leg?.acquired_ts ??
        leg?.acquiredTs ??
        leg?.acq_ts ??
        leg?.timestamp ??
        leg?.ts ??
        0
    );

    if (rule === "same_day") {
      offsets.push(0);
      acqLabels.push(acqTs ? fmtUtc(acqTs) : "Same-day");
    } else if (rule === "bed_and_breakfast_30d") {
      const legOffset = leg?.matchingOffsetDays;
      if (legOffset != null && Number.isFinite(Number(legOffset))) {
        const days = Number(legOffset);
        if (days >= 1 && days <= 30) offsets.push(days);
      } else if (disposalTsSec > 0) {
        const disposalDayKey = utcDayKey(disposalTsSec);
        const acqDayKey =
          leg?.acquisitionDayKey ||
          (acqTs > 0 ? utcDayKey(acqTs) : null);
        if (disposalDayKey && acqDayKey) {
          const days = utcCalendarDayDiff(disposalDayKey, acqDayKey);
          if (days != null && days >= 1 && days <= 30) offsets.push(days);
          else if (days === 0) offsets.push(0);
        }
      }
      acqLabels.push(acqTs ? fmtUtc(acqTs) : "30-day match");
    } else if (rule === "section_104") {
      acqLabels.push("Section 104 pool");
    } else if (rule === "nft_identifiable") {
      acqLabels.push(acqTs ? `${fmtUtc(acqTs)} · NFT identifiable` : "NFT identifiable");
    } else if (rule) {
      acqLabels.push(acqTs ? fmtUtc(acqTs) : "Section 104 pool");
    } else if (acqTs) {
      acqLabels.push(fmtUtc(acqTs));
    } else {
      acqLabels.push("Section 104 pool");
    }
  }

  const numericOffsets = offsets.filter((n) => Number.isFinite(n));
  const hasSameDay = offsets.includes(0);

  return {
    fifoLots: legs.length,
    offsetMin:
      numericOffsets.length > 0
        ? String(Math.min(...numericOffsets))
        : hasSameDay
          ? "0"
          : "",
    offsetMax:
      numericOffsets.length > 0
        ? String(Math.max(...numericOffsets))
        : hasSameDay
          ? "0"
          : "",
    acqMin: [...new Set(acqLabels.filter(Boolean))].join(" + ") || "Section 104 pool",
    acqMax: [...new Set(acqLabels.filter(Boolean))].join(" + ") || "",
  };
}

function legsStatsUs(row, disposalTsSec) {
  const legs = Array.isArray(row?.fifoLegs) ? row.fifoLegs : [];
  if (!legs.length) {
    return computeUkLegMatchingStats(row, disposalTsSec);
  }

  const num0 = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  const buy = legs.reduce(
    (s, l) => s + num0(l?.buyFiat ?? l?.buyEUR ?? l?.einstand ?? l?.costEUR),
    0
  );
  const sell = legs.reduce(
    (s, l) => s + num0(l?.sellFiat ?? l?.sellEUR ?? l?.verkauf ?? l?.proceedsEUR),
    0
  );
  const profit = legs.reduce((s, l) => {
    const p = num0(l?.legProfitFiat ?? l?.legProfitEUR ?? l?.profitEUR);
    if (p !== 0) return s + p;
    const be = num0(l?.buyFiat ?? l?.buyEUR);
    const se = num0(l?.sellFiat ?? l?.sellEUR);
    return s + (se - be);
  }, 0);

  const ukMatch = computeUkLegMatchingStats(row, disposalTsSec);

  return {
    ...ukMatch,
    buyEUR: buy,
    sellEUR: sell,
    profitEUR: profit,
    taxableProfitEUR: profit,
    nonTaxableProfitEUR: 0,
    shortTermProfitEUR: 0,
    longTermProfitEUR: 0,
  };
}

function getUkAnnualExemptAmount(year) {
  const y = Number(year);
  if (y <= 2022) return 12300;
  if (y === 2023) return 6000;
  return 3000;
}

function isUkIdentifiableNftCapitalRow(
  source = '',
  row = {}
) {
  const src =
    String(source || '')
      .trim()
      .toUpperCase();

  const cls =
    String(
      row?.classification ||
      row?.type ||
      row?.Klassifizierung ||
      ''
    )
      .trim()
      .toLowerCase();

  const method =
    String(
      row?.costBasisMethod ||
      row?.matchingRule ||
      row?.ukMethod ||
      ''
    )
      .trim()
      .toUpperCase();

  return (
    src === 'NFT' &&
    (
      method ===
        'UK_NFT_IDENTIFIABLE' ||

      String(
        row?.matchingRule ||
        ''
      )
        .trim()
        .toLowerCase() ===
        'uk_nft_identifiable' ||

      cls ===
        'sale_payment' ||

      cls ===
        'nft_transform_disposal'
    )
  );
}

function pickUkNftCapitalAsset(
  row = {}
) {
  const name =
    String(
      row?.nftName ||
      row?.nft_name ||
      row?.name ||
      row?.collectionName ||
      row?.collection_name ||
      'NFT'
    ).trim();

  const contract =
    String(
      row?.nftContract ||
      row?.nft_contract ||
      row?.contract ||
      row?.contractAddress ||
      row?.contract_address ||
      ''
    )
      .trim()
      .toLowerCase();

  const tokenId =
    row?.tokenId ??
    row?.nft_token_id ??
    row?.token_id ??
    row?.relatedMintTokenId ??
    '';

  const id =
    String(
      tokenId ?? ''
    ).trim();

  if (
    id &&
    id !== '—'
  ) {
    if (
      name &&
      name !== '—' &&
      name.toUpperCase() !==
        'NFT'
    ) {
      return `${name} #${id}`;
    }

    if (contract) {
      return (
        `NFT:${contract}:${id}`
      );
    }

    return `NFT #${id}`;
  }

  if (contract) {
    return `NFT:${contract}`;
  }

  return (
    name &&
    name !== '—'
      ? name
      : 'NFT'
  );
}

function pickUkCapitalAsset(
  source = '',
  row = {}
) {
  if (
    isUkIdentifiableNftCapitalRow(
      source,
      row
    )
  ) {
    return (
      pickUkNftCapitalAsset(
        row
      )
    );
  }

  return pickToken(row);
}

function pickUkCapitalLegalReference(
  source = '',
  row = {}
) {
  if (
    isUkIdentifiableNftCapitalRow(
      source,
      row
    )
  ) {
    const transformReference =
      String(
        row?.ukLegalReference ||
        ''
      ).trim();

    const parts = [
      'HMRC CRYPTO22100',
      'HMRC CRYPTO22200',
      transformReference,
      'Identifiable NFT disposal; not Section 104 pooled',
      'SA108 / Cryptoassets',
    ];

    const seen = new Set();
    const deduped = [];

    for (const part of parts) {
      for (const fragment of String(part || '')
        .split(';')
        .map((s) => s.trim())
        .filter(Boolean)) {
        const key = fragment.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);
        deduped.push(fragment);
      }
    }

    return deduped.join('; ');
  }

  return (
    'TCGA 1992 Sections 104, 105 and 106A; ' +
    'HMRC CRYPTO22200; ' +
    'same-day / 30-day / Section 104 matching; ' +
    'SA108 / Cryptoassets'
  );
}

function ukCapitalRowDayKey(
  row = {}
) {
  /*
   * NFTs:
   * getrennt identifizierbar,
   * KEINE Same-Day-/30-Day-Matching-Regeln.
   *
   * Deshalb ist jede NFT-Disposal ihre eigene
   * CGT computation.
   */
  if (
    row?._ukNftIdentifiable ===
    true
  ) {
    const eventKey =
      String(
        row?.Event_Key ||
        row?.eventKey ||
        ''
      )
        .trim()
        .toLowerCase();

    if (eventKey) {
      return (
        `nft-identifiable|${eventKey}`
      );
    }

    return [
      'nft-identifiable',

      String(
        row?.Asset ||
        ''
      )
        .trim()
        .toLowerCase(),

      Number(
        row?._sort ||
        0
      ),

      Number(
        row?._seq ||
        0
      ),
    ].join('|');
  }

  const asset = String(
    row.Asset ||
    row.asset ||
    row.token ||
    ""
  )
    .trim()
    .toUpperCase();

  let day = "";

  const ts = Number(row._sort || 0);

  if (Number.isFinite(ts) && ts > 0) {
    day = utcDayKey(ts);
  }

  if (!day) {
    const match = String(row.Datum_UTC || "").match(
      /^(\d{2})\/(\d{2})\/(\d{4})/
    );

    if (match) {
      const [, dd, mm, yyyy] = match;
      day = `${yyyy}-${mm}-${dd}`;
    }
  }

  return asset && day
    ? `${asset}|${day}`
    : "";
}

/**
 * SA108 boxes 13.1 / 13.4 / 13.5: same-day disposals of the same asset
 * are one CGT computation. Detail CSV rows stay unaggregated.
 */
export function summarizeUkSa108CapitalRows(
  rows = [],
  {
    proceedsKey = "Erlös_GBP_brutto",
    costKey = "Anschaffungskosten_GBP_brutto",
    profitKey = "GewinnVerlust_GBP",
  } = {}
) {
  const groups = new Map();

  for (const row of Array.isArray(rows) ? rows : []) {
    const key = ukCapitalRowDayKey(row);
    if (!key) continue;

    if (!groups.has(key)) {
      groups.set(key, {
        proceeds: 0,
        allowableCosts: 0,
        gainLoss: 0,
      });
    }

    const group = groups.get(key);

    group.proceeds += Number(row[proceedsKey] || 0);
    group.allowableCosts += Number(row[costKey] || 0);
    group.gainLoss += Number(row[profitKey] || 0);
  }

  const computations = [...groups.values()];

  return {
    disposalCount: computations.length,

    proceeds: computations.reduce(
      (sum, row) => sum + row.proceeds,
      0
    ),

    allowableCosts: computations.reduce(
      (sum, row) => sum + row.allowableCosts,
      0
    ),

    gainsBeforeLosses: computations.reduce(
      (sum, row) =>
        row.gainLoss > 0
          ? sum + row.gainLoss
          : sum,
      0
    ),

    lossesInYear: computations.reduce(
      (sum, row) =>
        row.gainLoss < 0
          ? sum + Math.abs(row.gainLoss)
          : sum,
      0
    ),

    netGainLoss: computations.reduce(
      (sum, row) => sum + row.gainLoss,
      0
    ),
  };
}

function summarizeRows(
  capitalRows,
  incomeRows,
  profitKey,
  taxableKey,
  incomeKey,
  yearInt,
  {
    proceedsKey = "Erlös_GBP_brutto",
    costKey = "Anschaffungskosten_GBP_brutto",
  } = {}
) {
  const sa108 = summarizeUkSa108CapitalRows(capitalRows, {
    proceedsKey,
    costKey,
    profitKey,
  });

  const miscellaneousIncomeFiat = (incomeRows || []).reduce(
    (s, r) => s + Number(r[taxableKey] ?? r[incomeKey] ?? 0),
    0
  );
  const annualExemptAmountFiat = getUkAnnualExemptAmount(yearInt);

  return {
    country: "UK",
    currency: "GBP",
    year: yearInt,
    taxYearLabel: formatUkTaxYearLabel(yearInt),
    disposalNetFiat:
      sa108.netGainLoss,

    disposalProceedsFiat:
      sa108.proceeds,

    allowableCostsFiat:
      sa108.allowableCosts,

    disposalGainFiat:
      sa108.gainsBeforeLosses,
    disposalLossFiat: sa108.lossesInYear > 0 ? -sa108.lossesInYear : 0,
    miscellaneousIncomeFiat,
    ukAnnualExemptAmountFiat: annualExemptAmountFiat,
    ukTaxableGainsAfterAllowanceFiat: Math.max(0, sa108.netGainLoss - annualExemptAmountFiat),
    ukMiscIncomeAllowanceFiat: 1000,
    miscIncomeReportingBand:
      miscellaneousIncomeFiat <= 1000
        ? "within_1000_misc_allowance"
        : miscellaneousIncomeFiat <= 2500
          ? "contact_hmrc"
          : "self_assessment_likely",
    disposalCount: sa108.disposalCount,
    incomeCount: incomeRows?.length || 0,
    reviewItemCount: 0,
    costBasisMethod: "UK_S104_POOLING",
    source: "uk_final_export_rows",
  };
}

function buildSectionBreakdown(capitalRows, incomeRows, profitKey, taxableKey, incomeKey) {
  const map = {
    VET: { swapProfit: 0, otherIncome: 0 },
    VTHO: { swapProfit: 0, otherIncome: 0 },
    "VIP-180": { swapProfit: 0, otherIncome: 0 },
    NFT: { swapProfit: 0, otherIncome: 0 },
    LIQUIDITY: { swapProfit: 0, otherIncome: 0 },
    EXCHANGE: { swapProfit: 0, otherIncome: 0 },
  };

  for (const r of capitalRows || []) {
    const src = String(r.Quelle || "").toUpperCase();
    const key = src === "VIP180" ? "VIP-180" : src;
    if (!map[key]) map[key] = { swapProfit: 0, otherIncome: 0 };
    map[key].swapProfit += Number(r[profitKey] || 0);
  }

  for (const r of incomeRows || []) {
    const src = String(r.Quelle || "").toUpperCase();
    const key = src === "VIP180" ? "VIP-180" : src;
    if (!map[key]) map[key] = { swapProfit: 0, otherIncome: 0 };
    map[key].otherIncome += Number(r[taxableKey] ?? r[incomeKey] ?? 0);
  }

  return map;
}

function incomeRowAuditIdentity(row = {}) {
  const ek = String(row.Event_Key || "").trim().toLowerCase();
  if (isUkIncomeMultiplicityExportRow(row) && ek) return ek;
  return normExportEventKey(row);
}

/** Count economic duplicate groups where the same real event appears more than once. */
function countIncomeAliasDuplicateGroups(incomeRows, incomeKey, taxableKey) {
  const groups = new Map();
  for (const r of incomeRows || []) {
    const fp = incomeAliasFingerprint(r, incomeKey, taxableKey);
    if (!groups.has(fp)) groups.set(fp, []);
    groups.get(fp).push(r);
  }

  let dupGroups = 0;
  for (const group of groups.values()) {
    if (group.length <= 1) continue;

    const seen = new Set();
    let duplicateRows = 0;
    for (const row of group) {
      const id = incomeRowAuditIdentity(row);
      if (seen.has(id)) duplicateRows++;
      else seen.add(id);
    }
    if (duplicateRows > 0) dupGroups++;
  }
  return dupGroups;
}

// ---------------------------------------------------------------------------
// Review rows derived from dataset tax rows (not only capital exclusions)
// ---------------------------------------------------------------------------

function hasUsableUkReviewMoney(row = {}) {
  const nums = [
    row?.taxableProfitFiat,
    row?.taxableProfitEUR,
    row?.grossProfitFiat,
    row?.grossProfitEUR,
    row?.profitFiat,
    row?.profitEUR,
    row?.amountFiat,
    row?.euroAmount,
    row?.totalFiat,
    row?.totalEUR,
  ];
  return nums.some((v) => {
    const n = Number(v);
    return Number.isFinite(n);
  });
}

function datasetRowNeedsReview(row = {}) {
  if (isActualTaxReviewRow(row)) return true;
  if (isBlockedTaxRow(row)) return true;
  if (hasUsableUkReviewMoney(row)) return false;
  return false;
}

function buildDerivedReviewRow(source, row = {}, ctx = {}) {
  const { yearInt = 0, language = "de", proceedsKey = "Erlös_GBP_brutto", taxYearLabel = "" } = ctx;
  const hash0 = pickHash(row);
  const hash = hash0 || `synthetic:review:${source}:${row?.eventId || row?.event_id || ""}`;
  const tsSec = toSec(pickRowTs(row));
  const eventUniq = pickUkEventUniq(row);
  const eventKey = String(
    row?.exportEventKey ||
      row?.eventKey ||
      row?.eventId ||
      row?.event_id ||
      (eventUniq ? `${hash}#${eventUniq}` : hash)
  ).toLowerCase();

  return {
    Steuerjahr: taxYearLabel || formatUkTaxYearLabel(yearInt),
    Quelle: source,
    Klassifizierung: pickCls(row),
    Asset: pickToken(row),
    Menge: pickAmount(row),
    Datum_UTC: fmtUtc(tsSec),
    [proceedsKey]: firstFinite(
      row?.grossSellFiat,
      row?.grossSellEUR,
      row?.amountFiat,
      row?.euroAmount,
      0
    ),
    Tx_Hash: hash0 ? pickDisplayHash(row, language) : hash,
    Event_Key: eventKey,
    Ausschlussgrund: pickExclusionReason(row),
    InSteuerSumme: language === "en" ? "No" : "Nein",
    FIFO_Lots: String(Array.isArray(row?.fifoLegs) ? row.fifoLegs.length : 0),
    _sort: tsSec,
  };
}

function rowHandledByCapitalExportPath(row = {}, source = "") {
  const cls = pickCls(row);
  if (source === "EXCHANGE") return true;
  if (source === "LIQUIDITY") return true;
  if (
    source === "NFT" &&
    (
      cls ===
        "sale_payment" ||

      cls ===
        "nft_transform_disposal" ||

      isUkNftPaymentTokenDisposal(
        cls
      )
    )
  ) {
    return true;
  }
  if (
    (source === "VET" || source === "VTHO" || source === "VIP-180") &&
    isUkTokenCapitalDisposal(cls)
  ) {
    return true;
  }
  return false;
}

/**
 * Additional review candidates from dataset tax rows (income-only, synthetic, etc.).
 * Merged into buildUkFinalExport so backend audit and frontend CSV share one set.
 */
export function deriveReviewRowsFromDataset(dataset = {}, yearInt = 0, options = {}) {
  const language = options.language || "de";
  const proceedsKey =
    options.proceedsKey || `Erlös_${options.fiatCurrency || "GBP"}_brutto`;
  const taxYearLabel = options.taxYearLabel || formatUkTaxYearLabel(yearInt);
  const derived = [];
  const seen = new Set();

  const push = (source, row) => {
    if (!row) return;
    if (rowHandledByCapitalExportPath(row, source)) return;
    const tsSec = toSec(pickRowTs(row));
    if (!rowInUkTaxYear(tsSec, yearInt) || !datasetRowNeedsReview(row)) return;

    const built = buildDerivedReviewRow(source, row, {
      yearInt,
      language,
      proceedsKey,
      taxYearLabel,
    });
    const key = normExportEventKey(built);
    if (!key || seen.has(key)) return;
    seen.add(key);
    derived.push(built);
  };

  for (const row of dataset?.vetTaxRows || []) push("VET", row);
  for (const row of dataset?.vthoTaxRows || []) push("VTHO", row);
  for (const row of dataset?.b3trTaxRows || []) push("VIP-180", row);
  for (const row of dataset?.nftTaxRows || []) push("NFT", row);
  for (const row of dataset?.exchangeTaxRows || []) push("EXCHANGE", row);

  for (const lr of dataset?.liquidityTaxRows || []) {
    if (Array.isArray(lr?.disposals)) {
      for (const row of lr.disposals) push("LIQUIDITY", row);
    } else {
      push("LIQUIDITY", lr);
    }
  }

  const syntheticReview =
    dataset?.fifo?.syntheticReviewEvents || dataset?.syntheticReviewEvents || [];
  for (const row of syntheticReview) push("REVIEW", row);

  return dedupeExportRowsByEventKey(derived);
}

// ---------------------------------------------------------------------------
// Main builder
// ---------------------------------------------------------------------------

/**
 * Builds final UK capital/income/review rows + summary from dataset.
 * This is the single source used by CSV export and UI summary cards.
 */
export function buildUkFinalExport(dataset = {}, year, options = {}) {
  const yearInt = Number(year);
  if (!dataset || !Number.isFinite(yearInt) || yearInt <= 0) {
    return null;
  }

  const {
    fiatCurrency = "GBP",
    language = "de",
    vetData = [],
    vthoData = [],
    b3trData = [],
    nftData = [],
    liquidityData = [],
    exchangeTaxRows = [],
  } = options;

  const PROFIT_KEY = `GewinnVerlust_${fiatCurrency}`;
  const PROCEEDS_KEY = `Erlös_${fiatCurrency}_brutto`;
  const COST_KEY = `Anschaffungskosten_${fiatCurrency}_brutto`;
  const TAXABLE_KEY = `Steuerpflichtig_${fiatCurrency}`;
  const INCOME_KEY = `Einnahmen_${fiatCurrency}_brutto`;

  const taxYearLabel = formatUkTaxYearLabel(yearInt);
  const inYear = (tsSec) => rowInUkTaxYear(tsSec, yearInt);

  const vetRows = Array.isArray(dataset?.vetTaxRows) ? dataset.vetTaxRows : vetData;
  const vthoRows = applyVthoGeneratedDisplayRollup(
    Array.isArray(dataset?.vthoTaxRows) ? dataset.vthoTaxRows : vthoData
  );
  const b3trRows = Array.isArray(dataset?.b3trTaxRows) ? dataset.b3trTaxRows : b3trData;
  const nftRows = Array.isArray(dataset?.nftTaxRows) ? dataset.nftTaxRows : nftData;
  const liqRows = Array.isArray(dataset?.liquidityTaxRows)
    ? dataset.liquidityTaxRows
    : liquidityData;
  const exRows = Array.isArray(dataset?.exchangeTaxRows)
    ? dataset.exchangeTaxRows
    : exchangeTaxRows;

  const includedCapital = [];
  const excludedReview = [];
  const incomeRaw = [];
  let seq = 0;

  const pushCapital = (source, r) => {
    const hash0 = pickHash(r);
    const hash = hash0 || `synthetic:${source}:${seq}`;
    const txHashDisplay = hash0 ? pickDisplayHash(r, language) : hash;
    const rowSeq = seq;
    const eventUniq = pickUkEventUniq(r);
    const eventKey = resolveUkCapitalEventKey({
      hash,
      eventUniq,
      rowSeq,
      eventId: r?.eventId || r?.exportEventKey || r?.eventKey || "",
    });

    const tsSec = toSec(pickRowTs(r));
    if (!inYear(tsSec)) return;

    const cls =
      pickCls(r);

    const token =
      pickToken(r);

    const amount =
      pickAmount(r);

    const leg =
      legsStatsUs(
        r,
        tsSec
      );

    const capitalAsset =
      pickUkCapitalAsset(
        source,
        r
      );

    const isIdentifiableNft =
      isUkIdentifiableNftCapitalRow(
        source,
        r
      );

    const legalReference =
      pickUkCapitalLegalReference(
        source,
        r
      );

    const sale = pickFiatSell(r) ?? pickFiatTotal(r) ?? firstFinite(leg.sellEUR, 0);
    const cost = pickFiatBuy(r) ?? firstFinite(leg.buyEUR, 0);
    const taxable = pickFiatTaxable(r) ?? firstFinite(leg.taxableProfitEUR, 0);
    const profitFromRow = pickFiatGrossProfit(r);
    const profitFromSaleCost =
      Number.isFinite(sale) && Number.isFinite(cost) ? sale - cost : NaN;
    const grossProfitLooksStale =
      Number.isFinite(profitFromRow) &&
      Math.abs(profitFromRow) <= 1e-12 &&
      Math.abs(taxable) > 1e-8 &&
      Number.isFinite(profitFromSaleCost) &&
      Math.abs(profitFromSaleCost - taxable) <= 0.05;
    const profit = grossProfitLooksStale
      ? profitFromSaleCost
      : Number.isFinite(profitFromRow)
        ? profitFromRow
        : firstFinite(leg.profitEUR, profitFromSaleCost, 0);

    const sortMeta = { _sort: tsSec, _seq: seq++ };

    if (isExcludedFromFinalTaxExport(r, true, "capital")) {
      excludedReview.push({
        Steuerjahr: taxYearLabel,
        Quelle: source,
        Klassifizierung: cls,
        Asset: capitalAsset,
        Menge: amount,
        Datum_UTC: fmtUtc(tsSec),
        [PROCEEDS_KEY]: sale,
        Tx_Hash: txHashDisplay,
        Event_Key: eventKey,
        Ausschlussgrund: pickExclusionReason(r),
        InSteuerSumme: language === "en" ? "No" : "Nein",
        FIFO_Lots: String(leg.fifoLots || 0),
        ...sortMeta,
      });
      return;
    }

    const identity =
      extractEventIdentityFields(r);

    const tokenContract =
      identity.token_contract ||
      String(
        r?.nftContract ||
        r?.nft_contract ||
        r?.tokenContract ||
        r?.token_contract ||
        r?.contract ||
        ''
      ).toLowerCase() ||
      '';

    const canonicalEventKey =
      identity.canonical_event_key ||
      String(
        r?.canonicalEventKey ||
        r?.canonical_event_key ||
        r?.eventKey ||
        r?.eventId ||
        eventKey
      );

    const disposalEventKey =
      identity.disposal_event_key ||
      String(
        r?.nftTransformDisposalEventKey ||
        r?.disposalEventKey ||
        r?.disposal_event_key ||
        canonicalEventKey
      );

    const fifoLegIndex =
      firstFinite(
        r?.nftTransformSourceOrdinal,
        r?.nftTransformSourceIndex,
        r?.fifoLegIndex,
        r?.legIndex,
        identity.fifo_leg_index
      );

    includedCapital.push({
      Steuerjahr: taxYearLabel,
      Steuerkategorie: "disposals",
      Rechtsgrundlage:
        legalReference,
      Quelle: source,
      Klassifizierung: cls,
      Asset:
        capitalAsset,
      Menge: amount,
      Datum_UTC: fmtUtc(tsSec),
      [PROCEEDS_KEY]: sale,
      [COST_KEY]: cost,
      [PROFIT_KEY]: profit,
      [TAXABLE_KEY]: taxable,
      Haltezeit_min_Ta: leg.offsetMin,
      Haltezeit_max_Ta: leg.offsetMax,
      Anschaffungsdatum_min_UTC: leg.acqMin,
      Anschaffungsdatum_max_UTC: leg.acqMax,
      FIFO_Lots:
        String(
          leg.fifoLots || 0
        ),
      Event_Key:
        eventKey,
      Tx_Hash:
        txHashDisplay,
      clause_index:
        identity.clause_index ??
        "",
      event_index:
        identity.event_index ??
        "",
      log_index:
        identity.log_index ??
        "",
      token_contract:
        identity.token_contract ||
        tokenContract ||
        "",
      canonical_event_key:
        identity.canonical_event_key ||
        canonicalEventKey ||
        "",
      disposal_event_key:
        identity.disposal_event_key ||
        disposalEventKey ||
        "",
      fifo_leg_index:
        identity.fifo_leg_index ??
        (
          Number.isFinite(
            fifoLegIndex
          )
            ? fifoLegIndex
            : ""
        ),
      _ukNftIdentifiable:
        isIdentifiableNft,
      ...sortMeta,
    });
  };

  const pushTokenDisposal = (source, r) => {
    const cls = pickCls(r);
    // NFT mint payment-token S104 disposals live in nftTaxRows and are routed
    // via pushNftMintPaymentTokenDisposal — never double-count from token tables.
    if (isUkNftPaymentTokenDisposal(cls)) return;
    if (isUkTokenCapitalDisposal(cls)) pushCapital(source, r);
  };

  const pushNftMintPaymentTokenDisposal =
    (r) => {
      const cls =
        pickCls(r);

      if (
        !isUkNftPaymentTokenDisposal(
          cls
        )
      ) {
        return;
      }

      const method =
        String(
          r?.costBasisMethod ||
          ''
        ).toUpperCase();

      /*
       * UK_NFT_IDENTIFIABLE =
       * NFT Acquisition selbst.
       *
       * UK_S104_POOLING =
       * Payment-Token Disposal.
       *
       * Nur letzteres gehört in Capital Gains.
       */
      const isDedicatedPaymentDisposal =
        r
          ?.ukNftPaymentTokenDisposal ===
          true ||
        method ===
          'UK_S104_POOLING';

      if (
        !isDedicatedPaymentDisposal
      ) {
        return;
      }

      const tsSec =
        toSec(
          pickRowTs(r)
        );

      const leg =
        legsStatsUs(
          r,
          tsSec
        );

      if (
        !hasUkNftMintPaymentDisposalMoney(
          r,
          leg
        )
      ) {
        return;
      }

      const paymentToken =
        String(
          r?.nftPaymentToken ||
          r?.token ||
          r?.tokenSymbol ||
          ''
        ).toUpperCase();

      const paymentAmount =
        firstFinite(
          r?.nftPaymentAmount,
          r?.amount,
          r?.qty,
          r?.quantity,
          0
        );

      const source =
        pickCapitalSourceFromPaymentToken(
          paymentToken,
          'NFT'
        );

      /*
       * IMPORTANT:
       *
       * r ist bereits die finale UK-S104-Disposal-Row.
       *
       * grossBuyFiat enthält:
       *
       *   S104 token cost
       * + allowable disposal fee allocation
       *
       * fifoLegs beschreiben dagegen ausschließlich
       * die Matching-Herkunft der Token-Basis.
       *
       * Deshalb dürfen die Parent-Money-Fields hier
       * NICHT erneut aus fifoLegs rekonstruiert werden.
       */
      const paymentRow = {
        ...r,

        token:
          paymentToken,

        tokenSymbol:
          paymentToken,

        amount:
          paymentAmount,

        nftPaymentToken:
          paymentToken,

        nftPaymentAmount:
          paymentAmount,
      };

      pushCapital(
        source,
        paymentRow
      );
  };

  for (const r of vetRows) pushTokenDisposal("VET", r);
  for (const r of vthoRows) pushTokenDisposal("VTHO", r);
  for (const r of b3trRows) pushTokenDisposal("VIP-180", r);

  for (const r of exRows) pushCapital("EXCHANGE", r);

  for (const lr of liqRows) {
    const disposals = Array.isArray(lr?.disposals) ? lr.disposals : [];

    const parentExcluded = isExcludedFromFinalTaxExport(lr, true, "capital");
    const parentReviewReason =
      lr?.reviewReason ||
      lr?.liquidityParentReviewReason ||
      (lr?.partialPricing ? "LIQUIDITY_UNDERLYING_PRICE_MISSING" : null) ||
      (String(lr?.auditStatus || "").toLowerCase() === "fail"
        ? "LIQUIDITY_AUDIT_FAILED"
        : null);

    for (const d of disposals) {
      const row = parentExcluded
        ? {
            ...d,
            needsReview: true,
            reviewRequired: true,
            requiresManualReview: true,
            reviewReason:
              d?.reviewReason || parentReviewReason || "LIQUIDITY_AUDIT_FAILED",

            taxComputationSkipped: true,
            excludedFromTaxTotals: true,
            includedInTaxTotals: false,

            partialPricing: lr?.partialPricing === true || d?.partialPricing === true,
            pricingMissing: lr?.pricingMissing === true || d?.pricingMissing === true,
            priceMissing: lr?.priceMissing === true || d?.priceMissing === true,
            missingFiatPrice: lr?.missingFiatPrice === true || d?.missingFiatPrice === true,

            auditStatus: lr?.auditStatus || d?.auditStatus || "fail",
            auditReasons: lr?.auditReasons || d?.auditReasons || [],
            liquidityParentHash: lr?.hash || d?.hash,
          }
        : d;

      pushCapital("LIQUIDITY", row);
    }
  }

  for (const r of nftRows) {
    const cls = pickCls(r);
    if (cls === "sale_payment" || cls === "nft_transform_disposal") {
      pushCapital("NFT", r);
      continue;
    }
    if (isNftCapitalDisposalClass(cls)) {
      pushNftMintPaymentTokenDisposal(r);
    }
  }

  const pushIncome = (source, r) => {
    const tsSec = toSec(pickRowTs(r));
    if (!inYear(tsSec)) return;
    if (isExcludedFromFinalTaxExport(r, true)) return;

    const cls = pickCls(r);
    if (!UK_INCOME_TYPES.has(cls)) return;

    const hash0 = pickHash(r);
    const hash = hash0 || `synthetic:${source}:${seq}`;
    const txHashDisplay = hash0 ? pickDisplayHash(r, language) : hash;
    const rowSeq = seq;
    const eventUniq = pickUkEventUniq(r);
    const hashLc = hash.toLowerCase();
    const rawEventKey = resolveUkCapitalEventKey({
      hash,
      eventUniq,
      rowSeq,
      eventId: r?.eventId || r?.exportEventKey || r?.eventKey || "",
    });
    const eventKey = normalizeUkIncomeEventKeyForDedup(rawEventKey, hashLc) || rawEventKey;

    const rowSource = resolveRowSource(source, r);
    const incomeAmt =
      (isSyntheticVthoRow(r) || cls === "generated"
        ? pickSyntheticVthoIncomeFiat(r)
        : null) ??
      pickFiatTotal(r) ??
      firstFinite(r?.euroAmount, r?.totalEUR, r?.valueEUR, 0);

    const identity =
      extractEventIdentityFields(
        r
      );

    incomeRaw.push({
      Steuerjahr: taxYearLabel,
      Steuerkategorie: "other_income",
      Rechtsgrundlage: "HMRC Cryptoassets Manual: miscellaneous income; Self Assessment",
      Quelle: rowSource,
      Klassifizierung: normalizeIncomeCls(rowSource, r),
      Asset: pickToken(r),
      Menge: pickAmount(r),
      Datum_UTC: fmtUtc(tsSec),
      [INCOME_KEY]: incomeAmt,
      [TAXABLE_KEY]: incomeAmt,
      Tx_Hash: txHashDisplay,
      Event_Key: eventKey,
      clause_index:
        identity.clause_index ??
        "",
      event_index:
        identity.event_index ??
        "",
      log_index:
        identity.log_index ??
        "",
      token_contract:
        identity.token_contract ||
        "",
      canonical_event_key:
        identity.canonical_event_key ||
        String(
          r?.canonicalEventKey ||
          r?.canonical_event_key ||
          eventKey ||
          ""
        ),
      _sort: tsSec,
      _seq: seq++,
    });
  };

  for (const r of vetRows) pushIncome("VET", r);
  for (const r of vthoRows) pushIncome("VTHO", r);
  for (const r of b3trRows) pushIncome("VIP-180", r);

  const sortRows = (a, b) =>
    (b._sort || 0) - (a._sort || 0) || (a._seq || 0) - (b._seq || 0);

  includedCapital.sort(sortRows);
  excludedReview.sort(sortRows);
  incomeRaw.sort(sortRows);

  const capitalRows = dedupeExportRowsByEventKey(includedCapital);

  const incomeAfterAlias = dedupeUkIncomeAliasRows(incomeRaw, INCOME_KEY, TAXABLE_KEY);
  const incomeRows = dedupeExportRowsByEventKey(incomeAfterAlias);

  const derivedReview = deriveReviewRowsFromDataset(dataset, yearInt, {
    language,
    fiatCurrency,
    proceedsKey: PROCEEDS_KEY,
    taxYearLabel,
  });

  const reviewRows = dedupeExportRowsByEventKey([...excludedReview, ...derivedReview]);

  const summary = summarizeRows(
    capitalRows,
    incomeRows,
    PROFIT_KEY,
    TAXABLE_KEY,
    INCOME_KEY,
    yearInt,
    {
      proceedsKey: PROCEEDS_KEY,
      costKey: COST_KEY,
    }
  );
  summary.reviewItemCount = reviewRows.length;

  const sectionBreakdown = buildSectionBreakdown(
    capitalRows,
    incomeRows,
    PROFIT_KEY,
    TAXABLE_KEY,
    INCOME_KEY
  );

  const audit = {
    capitalRows: capitalRows.length,
    capitalNet: summary.disposalNetFiat,
    incomeRows: incomeRows.length,
    incomeNet: summary.miscellaneousIncomeFiat,
    reviewRows: reviewRows.length,
    badCapitalRows: capitalRows.filter((r) => {
      const proceeds = Number(r[PROCEEDS_KEY] || 0);
      const cost = Number(r[COST_KEY] || 0);
      const gain = Number(r[PROFIT_KEY] || 0);
      return Math.abs(proceeds - cost - gain) > 0.01;
    }).length,
    duplicateIncomeGroups: countIncomeAliasDuplicateGroups(incomeRaw, INCOME_KEY, TAXABLE_KEY),
    duplicateIncomeGroupsAfterDedupe: countIncomeAliasDuplicateGroups(
      incomeRows,
      INCOME_KEY,
      TAXABLE_KEY
    ),
    reviewIncluded: reviewRows.filter((r) =>
      String(r.InSteuerSumme || "").toLowerCase().includes("ja")
    ).length,
  };

  const capitalProceedsTotal =
    capitalRows.reduce(
      (sum, row) =>
        sum +
        Number(
          row[PROCEEDS_KEY] ||
          0
        ),
      0
    );

  const capitalCostsTotal =
    capitalRows.reduce(
      (sum, row) =>
        sum +
        Number(
          row[COST_KEY] ||
          0
        ),
      0
    );

  const capitalNetTotal =
    capitalRows.reduce(
      (sum, row) =>
        sum +
        Number(
          row[PROFIT_KEY] ||
          0
        ),
      0
    );

  const summaryReconciliation = {
    proceedsDelta:
      capitalProceedsTotal -
      Number(
        summary
          .disposalProceedsFiat ||
        0
      ),

    costsDelta:
      capitalCostsTotal -
      Number(
        summary
          .allowableCostsFiat ||
        0
      ),

    netDelta:
      capitalNetTotal -
      Number(
        summary
          .disposalNetFiat ||
        0
      ),

    gainsLossesDelta:
      (
        Number(
          summary
            .disposalGainFiat ||
          0
        ) +
        Number(
          summary
            .disposalLossFiat ||
          0
        )
      ) -
      Number(
        summary
          .disposalNetFiat ||
        0
      ),
  };

  summaryReconciliation.passed =
    Math.abs(
      summaryReconciliation
        .proceedsDelta
    ) <= 1e-8 &&
    Math.abs(
      summaryReconciliation
        .costsDelta
    ) <= 1e-8 &&
    Math.abs(
      summaryReconciliation
        .netDelta
    ) <= 1e-8 &&
    Math.abs(
      summaryReconciliation
        .gainsLossesDelta
    ) <= 1e-8;

  audit.summaryReconciliation =
    summaryReconciliation;

  if (
    !summaryReconciliation.passed
  ) {
    console.error(
      'UK_FINAL_EXPORT_RECONCILIATION_FAILED',
      summaryReconciliation
    );

    const strictUkExport =
      typeof process !==
        'undefined' &&
      String(
        process
          ?.env
          ?.UK_FINAL_EXPORT_STRICT ||
        ''
      ) === '1';

    if (strictUkExport) {
      throw new Error(
        'UK_FINAL_EXPORT_RECONCILIATION_FAILED'
      );
    }
  }

  // Lightweight gas-fee reverse completeness snapshot (full FAIL gate lives in ukTaxAudit).
  {
    const isFee = (cls) => {
      const c = String(cls || "").toLowerCase();
      return (
        c === "gas_fee" ||
        c === "gas_abstraction_fee" ||
        c === "dex_aggregator_fee"
      );
    };
    const sourceFeeRows = [
      ...(dataset?.vthoTaxRows || []),
      ...(dataset?.vetTaxRows || []),
      ...(dataset?.b3trTaxRows || []),
    ].filter(
      (r) =>
        isFee(r?.classification || r?.type) &&
        rowInUkTaxYear(toSec(pickRowTs(r)), yearInt)
    );
    const capitalFee = capitalRows.filter((r) => isFee(r?.Klassifizierung));
    const reviewFee = reviewRows.filter((r) => isFee(r?.Klassifizierung));
    const sourceQty = sourceFeeRows.reduce(
      (s, r) => s + Number(r?.amount ?? r?.quantity ?? 0),
      0
    );
    const exportedQty =
      capitalFee.reduce((s, r) => s + Number(r?.Menge || 0), 0) +
      reviewFee.reduce((s, r) => s + Number(r?.Menge || 0), 0);
    const sourceKeys = new Set(
      sourceFeeRows
        .map((r) => `${String(r?.hash || r?.txid || "").toLowerCase()}|${String(r?.classification || r?.type || "").toLowerCase()}`)
        .filter((k) => !k.startsWith("|") && !k.endsWith("|"))
    );
    const exportKeys = [
      ...capitalFee.map((r) => `${String(r?.Tx_Hash || "").toLowerCase()}|${String(r?.Klassifizierung || "").toLowerCase()}`),
      ...reviewFee.map((r) => `${String(r?.Tx_Hash || "").toLowerCase()}|${String(r?.Klassifizierung || "").toLowerCase()}`),
    ];
    const exportKeyCounts = new Map();
    for (const k of exportKeys) {
      exportKeyCounts.set(k, (exportKeyCounts.get(k) || 0) + 1);
    }
    let missingRows = 0;
    for (const k of sourceKeys) {
      if (!exportKeyCounts.has(k)) missingRows += 1;
    }
    let duplicateRows = 0;
    for (const [k, n] of exportKeyCounts.entries()) {
      if (n > 1) duplicateRows += 1;
      if (!sourceKeys.has(k)) duplicateRows += 1;
    }
    audit.gasFees = {
      sourceRows: sourceFeeRows.length,
      capitalRows: capitalFee.length,
      reviewRows: reviewFee.length,
      missingRows,
      duplicateRows,
      sourceQuantity: sourceQty,
      exportedQuantity: exportedQty,
      quantityDelta: sourceQty - exportedQty,
    };
  }

  return {
    capitalRows,
    incomeRows,
    reviewRows,
    summary,
    sectionBreakdown,
    audit,
    keys: { PROFIT_KEY, PROCEEDS_KEY, COST_KEY, TAXABLE_KEY, INCOME_KEY },
  };
}
